import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCartShopping, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { buildProductGallery, resolveImageSrc } from '../../utils/productGallery';
import { PLACEHOLDER_350 } from '../../utils/imagePlaceholder';
import { fetchWithAuth } from '../../utils/authUtils';
import { addToCart, getCart } from '../../utils/cartStore';
import { isProductInStock, productStockQty, stockStatusClass, stockStatusLabel } from '../../utils/productStock';
import './ProductDetailPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [cartMessage, setCartMessage] = useState('');

  useEffect(() => {
    fetchProductDetail();
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const stock = productStockQty(product);
    const cartQty = getCart().find((item) => Number(item.productId) === Number(product.id))?.quantity || 0;
    const max = Math.max(0, Math.min(99, stock - cartQty));
    setQuantity((prev) => {
      const n = Math.max(1, Number(prev) || 1);
      if (max < 1) return 1;
      return Math.min(n, max);
    });
  }, [product]);

  const fetchProductDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/products/${id}`);
      const data = await response.json();

      if (data.success) {
        const item = data.data || {};
        const images = buildProductGallery(item);
        setProduct({ ...item, images });
        setActiveIndex(0);
      } else {
        setError(data.message || 'Lỗi khi tải sản phẩm');
      }
    } catch (err) {
      setError('Không thể kết nối đến server');
    } finally {
      setLoading(false);
    }
  };

  const stockQty = product ? productStockQty(product) : 0;
  const inStock = product ? isProductInStock(product) : false;
  const inCart = product
    ? (getCart().find((item) => Number(item.productId) === Number(product.id))?.quantity || 0)
    : 0;
  const maxBuy = Math.max(0, Math.min(99, stockQty - inCart));

  const cartProduct = () => ({
    id: product.id,
    name: product.name,
    price: product.price,
    stockQty,
    imageUrl: (product.images && product.images[0]) || product.image_url || '',
  });

  const handleAddToCart = () => {
    const result = addToCart(cartProduct(), quantity);
    if (!result.ok) {
      setCartMessage(result.message || 'Không thêm được vào giỏ');
      return;
    }
    setCartMessage('Đã thêm vào giỏ hàng');
  };

  const handleOrderNow = () => {
    const result = addToCart(cartProduct(), quantity);
    if (!result.ok) {
      setCartMessage(result.message || 'Không đặt được hàng');
      return;
    }
    navigate('/shop/cart');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN').format(price);
  };

  const images = product?.images || [];
  const activeImage = images[activeIndex] || '';

  const showPrevImage = () => {
    if (!images.length) return;
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const showNextImage = () => {
    if (!images.length) return;
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Đang tải sản phẩm...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-page">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => navigate('/shop/products')} className="btn-retry">
            Quay lại cửa hàng
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="error-state">
          <p>Không tìm thấy sản phẩm</p>
          <button onClick={() => navigate('/shop/products')} className="btn-retry">
            Quay lại cửa hàng
          </button>
        </div>
      </div>
    );
  }

  const displayTitle = (product.name || product.ext_title || 'Sản phẩm').trim();
  const subtitle = product.ext_title && product.ext_title.trim() !== displayTitle
    ? product.ext_title.trim()
    : '';

  return (
    <div className="product-detail-page">
      <div className="container">
        <button
          className="btn-back"
          onClick={() => navigate('/shop/products')}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Quay lại cửa hàng
        </button>

        <div className="product-meta">
          <div className="pd-gallery">
            <div className="pd-gallery-main">
              {images.length > 1 && (
                <>
                  <button type="button" className="pd-gallery-nav prev" onClick={showPrevImage} aria-label="Ảnh trước">
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <button type="button" className="pd-gallery-nav next" onClick={showNextImage} aria-label="Ảnh sau">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </>
              )}
              <img
                src={activeImage ? resolveImageSrc(activeImage) : PLACEHOLDER_350}
                alt={displayTitle}
                onError={(e) => {
                  e.target.src = PLACEHOLDER_350;
                }}
              />
            </div>

            {images.length > 0 && (
              <div className="pd-gallery-meta">
                <span>{activeIndex + 1} / {images.length} ảnh</span>
              </div>
            )}

            {images.length > 0 && (
              <div className="pd-gallery-thumbs">
                {images.map((url, index) => (
                  <button
                    key={`${url}-${index}`}
                    type="button"
                    className={`pd-gallery-thumb ${activeIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveIndex(index)}
                  >
                    <img src={resolveImageSrc(url)} alt={`Ảnh ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-info">
            <div className="p-category">{product.category}</div>
            <h1 className="p-title">{displayTitle}</h1>
            {subtitle ? <p className="p-subtitle">{subtitle}</p> : null}
            <div className="p-price">₫{formatPrice(product.price)}</div>
            <p className={`p-stock ${stockStatusClass(stockQty)}`}>{stockStatusLabel(stockQty)}</p>
            {inCart > 0 ? <p className="p-stock-cart">Trong giỏ: {inCart}{maxBuy > 0 ? ` · Có thể thêm tối đa ${maxBuy}` : ''}</p> : null}
            <label className="qty-label">
              Số lượng
              <input
                type="number"
                min="1"
                max={Math.max(1, maxBuy || 1)}
                value={quantity}
                disabled={!inStock || maxBuy < 1}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </label>
            <div className="buy-actions">
              <button className="btn-buy-partner" onClick={handleOrderNow} disabled={!inStock || maxBuy < 1}>
                <FontAwesomeIcon icon={faCartShopping} /> {inStock && maxBuy > 0 ? 'Đặt hàng' : 'Hết hàng'}
              </button>
              <button className="btn-add-cart" type="button" onClick={handleAddToCart} disabled={!inStock || maxBuy < 1}>
                Thêm vào giỏ
              </button>
            </div>
            {cartMessage ? <p className="cart-message">{cartMessage}</p> : null}
          </div>
        </div>

        <div className="p-details-section">
          <h2>Mô tả chi tiết sản phẩm</h2>
          <div
            className="content"
            dangerouslySetInnerHTML={{ __html: product.ext_content || '<p>Chưa có thông tin chi tiết</p>' }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
